import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { plantsService } from "../services/plants.service";
import type {
	CreatePlantRequest,
	ErrorResponse,
	PlantListParams,
	UpdatePlantRequest,
} from "../types";

export const plantKeys = {
	all: ["plants"] as const,
	lists: () => [...plantKeys.all, "list"] as const,
	list: (params?: PlantListParams) => [...plantKeys.lists(), params] as const,
	details: () => [...plantKeys.all, "detail"] as const,
	detail: (id: string) => [...plantKeys.details(), id] as const,
};

export function usePlantList(params: PlantListParams) {
	return useQuery({
		queryKey: plantKeys.list(params),
		queryFn: () => plantsService.list(params),
		enabled: !!params.organizationId,
	});
}

export function usePlant(id: string) {
	return useQuery({
		queryKey: plantKeys.detail(id),
		queryFn: () => plantsService.getById(id),
		enabled: !!id,
	});
}

export function useCreatePlant() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreatePlantRequest) => plantsService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: plantKeys.lists() });
			message.success("Ekin muvaffaqiyatli qo'shildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			message.error(error.response?.data?.error?.message || "Ekin qo'shishda xatolik");
		},
	});
}

export function useUpdatePlant() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdatePlantRequest }) =>
			plantsService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: plantKeys.lists() });
			queryClient.invalidateQueries({ queryKey: plantKeys.detail(variables.id) });
			message.success("Ekin muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			message.error(error.response?.data?.error?.message || "Ekin yangilashda xatolik");
		},
	});
}

export function useDeletePlant() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => plantsService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: plantKeys.lists() });
			message.success("Ekin muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			message.error(error.response?.data?.error?.message || "Ekin o'chirishda xatolik");
		},
	});
}
